import React, { useEffect, useState } from 'react';
import {
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    MenuItem, Select, FormControl, InputLabel, Box, Chip, Typography, CircularProgress, Alert
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { OutlinedInput } from '@mui/material';
import { TaskDraftDTO } from '../../types/TaskDraftDTO';
import { Media } from '../../types/media'; // Добавлено
import { TaskDecompositionRequestDTO, DecompositionItemDTO } from '../../types/TaskDecompositionRequestDTO';
import { TaskStatus } from '../../types/task-status';
import { ManagerDTO } from '../../types/manager';
import { User } from '../../types/user';
import { Skill } from '../../types/skill'; // Добавлено
import { userService } from '../../services/userService';
import { managerService } from '../../services/managerService'; // Добавлено
import { taskService } from '../../services/taskService'; // Добавлено
import { skillService } from '../../services/skillService'; // Добавлено
import { mediaService } from '../../services/mediaService'; // Добавлено

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

interface TaskDecompositionModalProps {
    open: boolean;
    onClose: () => void;
    taskDraft: TaskDraftDTO | null;
    onDecomposeSuccess: () => void;
}

type CommonTaskDetailsState = {
    title: string;
    description: string;
    priceInKopecks?: number;
    deadline?: Date | null; // This is what DatePicker expects
    estimatedHours?: number;
    priority?: number;
    requirements?: string;
    neededUsers?: number;
    status?: TaskStatus; // Новое поле
    completionResult?: string; // Новое поле
    managerId?: string; // Новое поле
    selectedSkillIds?: string[]; // Добавлено для выбранных навыков
    mediaIds?: string[]; // Добавлено для выбранных медиа-файлов
};

const TaskDecompositionModal: React.FC<TaskDecompositionModalProps> = ({ open, onClose, taskDraft, onDecomposeSuccess }) => {
    const [commonTaskDetails, setCommonTaskDetails] = useState<CommonTaskDetailsState>({
        title: '',
        description: '',
        priority: 3, // Default priority
        status: TaskStatus.Created, // Default status for decomposed tasks
    });
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [selectedExistingMediaIds, setSelectedExistingMediaIds] = useState<string[]>([]); // Состояние для выбранных существующих медиа
    const [newlySelectedFiles, setNewlySelectedFiles] = useState<File[]>([]); // Состояние для новых загруженных файлов
    const [availableUsers, setAvailableUsers] = useState<User[]>([]);
    const [availableManagers, setAvailableManagers] = useState<ManagerDTO[]>([]);
    const [availableSkills, setAvailableSkills] = useState<Skill[]>([]); // Добавлено
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const getStatusDisplayName = (status?: TaskStatus) => {
        switch (status) {
            case TaskStatus.Created: return 'Создана';
            case TaskStatus.Assigned: return 'Назначена';
            case TaskStatus.Started: return 'В работе';
            case TaskStatus.Completed: return 'Завершена';
            case TaskStatus.Verified: return 'Проверена';
            case TaskStatus.ForRevision: return 'На доработку';
            case TaskStatus.Rejected: return 'Отклонена';
            case TaskStatus.Paid: return 'Оплачена';
            default: return status || 'Неизвестно';
        }
    };

    useEffect(() => {
        if (!open || !taskDraft) return; // Only fetch if modal is open and taskDraft is available

        const fetchDependencies = async () => {
            try {
                // Fetch all available users for assignment
                const usersResponse = await userService.getAllUsers();
                setAvailableUsers(usersResponse);

                // Fetch all available managers
                const managersResponse = await managerService.getAllManagers();
                setAvailableManagers(managersResponse);

                const skillsResponse = await skillService.getAllSkills(); // Загружаем все доступные навыки
                setAvailableSkills(skillsResponse.data);

                // Pre-fill common task details from taskDraft
                setCommonTaskDetails({
                    title: taskDraft.title || '',
                    description: taskDraft.description || '',
                    priceInKopecks: taskDraft.priceInKopecks,
                    deadline: taskDraft.deadline ? new Date(taskDraft.deadline) : null,
                    estimatedHours: taskDraft.estimatedHours ? Math.round(taskDraft.estimatedHours) : undefined,
                    priority: taskDraft.priority || 3,
                    requirements: taskDraft.requirements || '',
                    neededUsers: taskDraft.neededUsers,
                    status: TaskStatus.Created, // Default status for decomposed tasks
                    managerId: taskDraft.managerId || undefined, // Manager from TaskDraft
                    completionResult: '', // Default empty
                    selectedSkillIds: taskDraft.skills?.map(s => s.id!) || [],
                    mediaIds: taskDraft.media?.map(m => m.id!) || [], // Pre-fill mediaIds
                });
                setSelectedExistingMediaIds(taskDraft.media?.map(m => m.id!) || []); // Инициализация выбранных существующих медиа
                setNewlySelectedFiles([]); // Очистка новых файлов при открытии модала

            } catch (err) {
                setError('Не удалось загрузить данные для декомпозиции.');
                console.error('Ошибка при загрузке зависимостей:', err);
            } finally {
                setLoading(false);
            }
        };

        setLoading(true);
        fetchDependencies();
    }, [open, taskDraft]); // Re-run when modal opens or taskDraft changes

    const handleChangeCommonTaskDetails = <K extends keyof CommonTaskDetailsState>(field: K, value: CommonTaskDetailsState[K]) => {
        setCommonTaskDetails((prev: CommonTaskDetailsState) => ({ ...prev, [field]: value }));
    };

    const handleUserSelection = (userId: string) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
        );
    };

    const handleExistingMediaSelection = (mediaId: string) => {
        setSelectedExistingMediaIds((prev) =>
            prev.includes(mediaId) ? prev.filter((id) => id !== mediaId) : [...prev, mediaId]
        );
    };

    const handleNewFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setNewlySelectedFiles((prev) => [...prev, ...Array.from(event.target.files || [])]);
        }
    };

    const handleRemoveNewFile = (index: number) => {
        setNewlySelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleDecompose = async () => {
        if (!taskDraft) {
            setError('Черновик задачи не доступен.');
            return;
        }

        if (!commonTaskDetails.title || !commonTaskDetails.description) {
            setError('Пожалуйста, введите название и описание задачи.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let uploadedMediaIds: string[] = [];
            if (newlySelectedFiles.length > 0) {
                // Загружаем новые файлы и получаем их ID
                const uploadPromises = newlySelectedFiles.map(file =>
                    mediaService.uploadFile(file, taskDraft.id!) // Используем ID черновика для связи
                        .then(response => response.id) // Предполагаем, что API возвращает { id: string, ... }
                );
                uploadedMediaIds = await Promise.all(uploadPromises);
            }

            const finalMediaIds = [...selectedExistingMediaIds, ...uploadedMediaIds];

            const decompositionItems: DecompositionItemDTO[] = selectedUserIds.map(userId => ({
                assignedToUserId: userId,
            }));

            const requestPayload: TaskDecompositionRequestDTO = {
                originalTaskDraftId: taskDraft.id!,
                title: commonTaskDetails.title,
                description: commonTaskDetails.description,
                priceInKopecks: commonTaskDetails.priceInKopecks,
                deadline: commonTaskDetails.deadline ? commonTaskDetails.deadline.toISOString() : undefined,
                estimatedHours: commonTaskDetails.estimatedHours,
                priority: commonTaskDetails.priority || 3,
                requirements: commonTaskDetails.requirements,
                neededUsers: commonTaskDetails.neededUsers,
                status: commonTaskDetails.status || TaskStatus.Created, // Новое поле
                completionResult: commonTaskDetails.completionResult || undefined, // Новое поле
                managerId: commonTaskDetails.managerId || undefined, // Новое поле
                skillIds: commonTaskDetails.selectedSkillIds, // Добавлено поле для навыков
                mediaIds: finalMediaIds, // Передаем объединенный список идентификаторов медиа
                decompositionItems: decompositionItems,
            };

            await taskService.decomposeTask(requestPayload);
            onDecomposeSuccess();
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.title || 'Ошибка при декомпозиции задачи.');
            console.error('Ошибка при декомпозиции задачи:', err);
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Декомпозиция черновика задачи: {taskDraft?.title}</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <TextField
                    autoFocus
                    margin="normal"
                    fullWidth
                    label="Название задачи"
                    value={commonTaskDetails.title}
                    onChange={(e) => handleChangeCommonTaskDetails('title', e.target.value)}
                    required
                    size="small"
                />
                <TextField
                    margin="normal"
                    fullWidth
                    label="Описание задачи"
                    value={commonTaskDetails.description}
                    onChange={(e) => handleChangeCommonTaskDetails('description', e.target.value)}
                    multiline
                    rows={4}
                    required
                    size="small"
                />
                <TextField
                    margin="normal"
                    fullWidth
                    label="Бюджет в копейках"
                    type="number"
                    value={commonTaskDetails.priceInKopecks || ''}
                    onChange={(e) => handleChangeCommonTaskDetails('priceInKopecks', parseFloat(e.target.value))}
                    size="small"
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="Дедлайн"
                        value={commonTaskDetails.deadline}
                        onChange={(newValue) => handleChangeCommonTaskDetails('deadline', newValue)}
                        slots={{ textField: (params) => <TextField {...params} fullWidth margin="normal" size="small" /> }}
                    />
                </LocalizationProvider>
                <TextField
                    margin="normal"
                    fullWidth
                    label="Расчетное время выполнения (часы)"
                    type="number"
                    value={commonTaskDetails.estimatedHours || ''}
                    onChange={(e) => handleChangeCommonTaskDetails('estimatedHours', parseInt(e.target.value))}
                    size="small"
                />
                <TextField
                    margin="normal"
                    fullWidth
                    label="Приоритет (1-5)"
                    type="number"
                    inputProps={{ min: 1, max: 5 }}
                    value={commonTaskDetails.priority || ''}
                    onChange={(e) => handleChangeCommonTaskDetails('priority', parseInt(e.target.value))}
                    size="small"
                />
                <TextField
                    margin="normal"
                    fullWidth
                    label="Требования к исполнителю"
                    value={commonTaskDetails.requirements || ''}
                    onChange={(e) => handleChangeCommonTaskDetails('requirements', e.target.value)}
                    multiline
                    rows={3}
                    size="small"
                />
                <TextField
                    fullWidth
                    label="Результат выполнения"
                    value={commonTaskDetails.completionResult || ''}
                    onChange={(e) => handleChangeCommonTaskDetails('completionResult', e.target.value)}
                    margin="normal"
                    size="small"
                    multiline
                    rows={3}
                />
                <FormControl fullWidth margin="normal" size="small">
                    <InputLabel id="status-label">Статус задачи</InputLabel>
                    <Select
                        labelId="status-label"
                        value={commonTaskDetails.status || TaskStatus.Created}
                        label="Статус задачи"
                        onChange={(event) => handleChangeCommonTaskDetails('status', event.target.value as TaskStatus)}
                    >
                        {Object.values(TaskStatus).map((status) => (
                            <MenuItem key={status} value={status}>
                                {getStatusDisplayName(status)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                
                <Typography variant="h6" sx={{ mt: 3 }}>Назначение исполнителей и менеджеров</Typography>
                
                <FormControl fullWidth margin="normal" size="small">
                    <InputLabel>Выбор исполнителей (опционально)</InputLabel>
                    <Select
                        multiple
                        value={selectedUserIds}
                        onChange={(event) => {
                            const value = event.target.value as string[];
                            setSelectedUserIds(value);
                        }}
                        input={<OutlinedInput label="Выбор исполнителей (опционально)" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((userId) => {
                                    const user = availableUsers.find(u => u.id === userId);
                                    return user ? <Chip key={user.id} label={user.fullName} /> : null;
                                })}
                            </Box>
                        )}
                        MenuProps={MenuProps}
                    >
                        {availableUsers.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                                {user.fullName || user.personalData?.fullName || user.phone}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal" size="small">
                    <InputLabel id="manager-label">Менеджер (опционально)</InputLabel>
                    <Select
                        labelId="manager-label"
                        value={commonTaskDetails.managerId || ''}
                        label="Менеджер (опционально)"
                        onChange={(event) => handleChangeCommonTaskDetails('managerId', event.target.value as string)}
                    >
                        <MenuItem value="">— Не выбран —</MenuItem>
                        {availableManagers.map((manager) => (
                            <MenuItem key={manager.id} value={manager.id}>
                                {manager.fullName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal" size="small">
                    <InputLabel>Необходимые навыки (опционально)</InputLabel>
                    <Select
                        multiple
                        value={commonTaskDetails.selectedSkillIds || []}
                        onChange={(event) => {
                            const value = event.target.value as string[];
                            handleChangeCommonTaskDetails('selectedSkillIds', value);
                        }}
                        input={<OutlinedInput label="Необходимые навыки (опционально)" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((skillId) => {
                                    const skill = availableSkills.find(s => s.id === skillId);
                                    return skill ? <Chip key={skill.id} label={skill.title} /> : null;
                                })}
                            </Box>
                        )}
                        MenuProps={MenuProps}
                    >
                        {availableSkills.map((skill) => (
                            <MenuItem key={skill.id} value={skill.id}>
                                {skill.title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Typography variant="h6" sx={{ mt: 3 }}>Медиа-файлы</Typography>
                {taskDraft?.media && taskDraft.media.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Существующие файлы:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {taskDraft.media.map((media) => (
                                <Chip
                                    key={media.id}
                                    label={media.name}
                                    onClick={() => handleExistingMediaSelection(media.id!)}
                                    color={selectedExistingMediaIds.includes(media.id!) ? "primary" : "default"}
                                    variant={selectedExistingMediaIds.includes(media.id!) ? "filled" : "outlined"}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Загрузить новые файлы:</Typography>
                    <input
                        type="file"
                        multiple
                        onChange={handleNewFileChange}
                        style={{ display: 'block', marginTop: '8px' }}
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {newlySelectedFiles.map((file, index) => (
                            <Chip
                                key={index}
                                label={file.name}
                                onDelete={() => handleRemoveNewFile(index)}
                            />
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Отмена</Button>
                <Button onClick={handleDecompose} disabled={loading || !commonTaskDetails.title || !commonTaskDetails.description} variant="contained">
                    {loading ? <CircularProgress size={24} /> : 'Декомпозировать'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskDecompositionModal;
